/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import { Chevron } from '@ahamove/icons';
import { Nationwide } from '@ahamove/icons/pages';
import { useLocationStore } from '@ahm/react-map/store';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Stack,
  Text,
  Title,
  rem,
  Group,
  type ButtonProps,
  Popover,
  ActionIcon,
  ScrollArea,
  Box,
} from '@mantine/core';
import { ChevronLeft, InfoIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { useMemo } from 'react';
import ModalWrapper from '@/components/modals/modal-wrapper';
import { ROUTES } from '@/config/router';
import BookingAlert from '@/features/booking/components/booking-alert';
import BookingServiceInfoTooltip from '@/features/booking/components/services/booking-service-info-tooltip';
import { getTruckServiceErrorMessage } from '@/features/booking/components/services/truck-service-utils';
import { useHmService } from '@/features/booking/components/services/use-hm-service';
import TruckServiceSwiper from '@/features/booking/components/truck/truck-service/truck-service-swiper';
import { useDetectBanHour } from '@/hooks';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { getServiceType } from '@/lib/utils';
import { useLocaleSuffix, usePathname } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';
import { useDetectModalStore } from '@/stores/use-detect-modal-store';

type Props = ButtonProps;

export default function HmService(props: Props) {
  const isMobile = useIsMobile();
  const localeSuffix = useLocaleSuffix();
  const tBooking = useTranslations('booking');
  const pathname = usePathname();
  const paths = useLocationStore.use.paths();
  const tService = useTranslations('booking.service');
  const { isHMService } = useMemo(() => getServiceType(pathname), [pathname]);

  const {
    selectedService,
    isLoadingStore,
    isOpenPopupAllService,
    estimatedData,
  } = useBookingStore((state) => state);
  const { modal_id } = useDetectModalStore((state) => state);
  const { checkServiceInBanFn } = useDetectBanHour();
  const {
    onOpenPopupAllService,
    onClosePopupAllService,
    pickupPath,
    renderHmEstimatedServices,
    isDisabled,
  } = useHmService();

  // const { mutate: hmEstimatePackages } = useHmEstimatePackagesMutation({
  //   onSuccess: (data) => {
  //     console.log(data);
  //   },
  // });

  const errorEstimatedData = useMemo(() => {
    if (!selectedService) return null;
    const isServiceRestrictedByBan = !!checkServiceInBanFn(selectedService);

    return getTruckServiceErrorMessage(
      selectedService,
      isServiceRestrictedByBan,
      estimatedData?.error
    );
  }, [selectedService, estimatedData, checkServiceInBanFn]);

  if (!paths || paths.length < 2) return null;

  return (
    <>
      {!isMobile ? (
        <Card
          radius={rem(12)}
          withBorder
          style={{ border: '1px solid var(--mantine-color-neutral-1)' }}
        >
          <Group justify="space-between" align="center">
            <Title order={4}>{tService('choose_vehicle')}</Title>
            <Popover
              closeOnClickOutside={!modal_id}
              opened={isOpenPopupAllService}
              onChange={onClosePopupAllService}
              styles={{
                dropdown: {
                  filter: 'drop-shadow(0px 16px 48px rgba(0, 0, 0, 0.08))',
                  borderRadius: 12,
                  background: 'white',
                  padding: 0,
                  border: 'none',
                },
              }}
              zIndex={200}
              width={400}
              offset={16}
              position="right"
              shadow="md"
            >
              <Popover.Target>
                <Button
                  variant="transparent"
                  mr={rem(-16)}
                  fw={700}
                  disabled={isDisabled}
                  onClick={onOpenPopupAllService}
                >
                  {tService('view_all')}
                </Button>
              </Popover.Target>

              <Popover.Dropdown
                top={0}
                styles={{
                  dropdown: {
                    overflow: 'hidden',
                  },
                }}
              >
                <Group
                  pos="sticky"
                  top={0}
                  left={0}
                  justify="flex-start"
                  h={rem(60)}
                  w="100%"
                  p="md"
                  bg="white"
                  styles={{
                    root: {
                      zIndex: 100,
                      borderBottom: `1px solid var(--mantine-color-neutral-1)`,
                    },
                  }}
                >
                  <ActionIcon
                    size={rem(34)}
                    variant="transparent"
                    onClick={onClosePopupAllService}
                    c="neutral.5"
                  >
                    <ChevronLeft width={24} height={24} />
                  </ActionIcon>
                  <Title lh={rem(28)} order={3}>
                    {tBooking('service.choose_vehicle_type')}
                  </Title>
                </Group>
                <ScrollArea.Autosize
                  mah={pathname === ROUTES.nationwide ? rem(680 - 65) : '100vh'}
                  h="auto"
                >
                  {renderHmEstimatedServices()}
                </ScrollArea.Autosize>
              </Popover.Dropdown>
            </Popover>
          </Group>
          {isHMService && <TruckServiceSwiper />}

          {errorEstimatedData ? (
            <Box mt={rem(16)}>
              <BookingAlert
                title={errorEstimatedData?.title}
                description={errorEstimatedData?.description}
              />
            </Box>
          ) : null}
        </Card>
      ) : (
        <Button
          loading={isLoadingStore}
          disabled={isDisabled}
          fullWidth
          h="auto"
          mih={{ base: rem(48), md: rem(56) }}
          radius={rem(12)}
          pl={{ base: rem(8), md: rem(12) }}
          py={{ base: rem(8), md: rem(12) }}
          onClick={onOpenPopupAllService}
          px="md"
          variant="white"
          leftSection={
            <Avatar
              radius="sm"
              variant="outline"
              size={rem(32)}
              p={0}
              flex="none"
              src={selectedService?.icon_url}
              alt={selectedService?.name}
              styles={{
                image: { objectFit: 'contain' },
                placeholder: {
                  border: 'none',
                },
              }}
            >
              {pickupPath && selectedService ? (
                <Nationwide className="size-8" />
              ) : (
                <InfoIcon width={16} height={16} />
              )}
            </Avatar>
          }
          rightSection={
            selectedService ? (
              <Chevron className="size-5 md:size-6" />
            ) : undefined
          }
          justify="space-between"
          styles={{
            root: {
              overflow: 'visible',
            },
            label: {
              width: '100%',
            },
          }}
          {...props}
        >
          <Stack gap={rem(4)} ta="left" w="100%">
            {pickupPath && selectedService ? (
              <>
                <Title
                  order={isMobile ? 6 : 4}
                  fw={selectedService && pickupPath ? 700 : 500}
                  c="neutral.6"
                  className="truncate"
                >
                  {selectedService[`name_${localeSuffix}`]}{' '}
                  <BookingServiceInfoTooltip
                    isMobile={isMobile}
                    service={selectedService}
                  />
                </Title>
                {selectedService?.[`description_${localeSuffix}`] ? (
                  <Text size="sm" fw={500} c="neutral.7" truncate>
                    {selectedService[`description_${localeSuffix}`]}
                  </Text>
                ) : null}
              </>
            ) : null}

            {!pickupPath ? (
              <Text size="sm" fw={500} c="neutral.7" truncate>
                {tBooking('service.required_pickup_location')}
              </Text>
            ) : null}

            {pickupPath && !selectedService ? (
              <Text size="sm" fw={500} c="neutral.7" truncate>
                {tBooking('service.no_data')}
              </Text>
            ) : null}
          </Stack>
          {selectedService?.badge ? (
            <Badge
              tt="capitalize"
              size="md"
              radius="sm"
              pos="absolute"
              fw={600}
              top={rem(-8)}
              right={rem(-6)}
              bg={selectedService.badge.background_color ?? 'red.6'}
              c={selectedService.badge.text_color ?? 'white'}
            >
              {selectedService.badge[`description_${localeSuffix}`] || 'Mới'}
            </Badge>
          ) : null}
        </Button>
      )}

      {isMobile ? (
        <ModalWrapper
          // title={tBooking('service.choose_service')}
          title="Chọn loại xe"
          isOpen={isOpenPopupAllService}
          onClose={onClosePopupAllService}
          optionStyle={{
            headerStyle: {
              minHeight: rem(56),
              borderBottom: `1px solid var(--mantine-color-neutral-0)`,
              paddingLeft: rem(16),
            },
            bodyStyle: {
              padding: 0,
            },
          }}
        >
          {renderHmEstimatedServices()}
        </ModalWrapper>
      ) : null}
    </>
  );
}
